import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const accountKey = ["account"] as const;

/**
 * The address is why this screen exists: it is the only place a volunteer can discover which of
 * their addresses we have, which is behind most of the mismatches the inloglink door answers.
 */
export function useAccount() {
  return useQuery({
    queryKey: accountKey,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/account");
      if (!data) {
        throw new Error(
          i18n.t("account:errors.load", { status: response.status }),
        );
      }

      return data;
    },
  });
}

/** The QR before the code, because a code cannot be produced without it. */
export function useBeginTweedeFactor() {
  return useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/account/tweede-factor");
      if (!data) {
        throw new Error(
          i18n.t("account:errors.tweedeFactor", { status: response.status }),
        );
      }

      return data;
    },
  });
}

/**
 * Does not invalidate on success: the response carries recovery codes shown once and never
 * fetchable again, and a refetch would swap the card showing them.
 */
export function useSetWachtwoord() {
  return useMutation({
    mutationFn: async (body: { password: string; code: string }) => {
      const { data, response } = await api.POST("/api/account/wachtwoord", {
        body,
      });
      if (!data) {
        throw new Error(
          i18n.t("account:errors.wachtwoord", { status: response.status }),
        );
      }

      return data;
    },
  });
}

export function useChangeWachtwoord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { current: string; next: string }) => {
      const { data, response } = await api.PUT("/api/account/wachtwoord", {
        body,
      });
      if (!data) {
        throw new Error(
          i18n.t("account:errors.wachtwoord", { status: response.status }),
        );
      }

      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: accountKey }),
  });
}

/** The password and the second factor go together - see ADR-0066. A code proves the holder. */
export function useRemoveWachtwoord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { code: string }) => {
      const { data, response } = await api.DELETE("/api/account/wachtwoord", {
        body,
      });
      if (!data) {
        throw new Error(
          i18n.t("account:errors.wachtwoord", { status: response.status }),
        );
      }

      return data;
    },
    onSuccess: (data) => {
      if (data.status === "verwijderd") {
        void queryClient.invalidateQueries({ queryKey: accountKey });
      }
    },
  });
}
